import React, { useState, useEffect } from "react";

/**
 * PayoutWeekdayCard Component
 * 
 * Displays the user's assigned payout weekday and allows changes
 * with cooldown enforcement.
 */
const PayoutWeekdayCard = ({ beneficiaryType = "staff", compact = false }) => {
  const [weekdayInfo, setWeekdayInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState(false);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [selectedWeekday, setSelectedWeekday] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("ogestyle_token");
    return token ? { Authorization: `Bearer ${token}` } : null;
  };

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_BASE_URL || "";
  };

  const fetchWeekdayInfo = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(
        `${getApiBaseUrl()}/api/payout-weekday/${beneficiaryType}`,
        { headers }
      );

      if (response.ok) {
        const data = await response.json();
        setWeekdayInfo(data);
        setSelectedWeekday(data.currentWeekday);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWeekdayInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beneficiaryType]);

  const handleChangeWeekday = async () => {
    if (!selectedWeekday || selectedWeekday === weekdayInfo.currentWeekday) {
      setError("Please select a different weekday");
      return;
    }

    try {
      setChanging(true);
      setError("");
      setSuccess("");

      const headers = {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      };

      const response = await fetch(
        `${getApiBaseUrl()}/api/payout-weekday/${beneficiaryType}`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify({ weekday: selectedWeekday }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message);
        setShowChangeModal(false);
        await fetchWeekdayInfo();
      } else {
        setError(data.message || "Failed to change payout weekday");
      }
    } catch (err) {
      setError("An error occurred while changing your payout weekday");
    } finally {
      setChanging(false);
    }
  };

  const capitalizeWeekday = (day) => {
    if (!day) return "";
    return day.charAt(0).toUpperCase() + day.slice(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div style={{ padding: compact ? "12px" : "20px", textAlign: "center" }}>
        <div style={{ color: "#6b7280" }}>Loading payout schedule...</div>
      </div>
    );
  }

  if (!weekdayInfo) {
    return (
      <div
        style={{
          background: "#fef2f2",
          border: "1px solid #ef4444",
          borderRadius: "8px",
          padding: "12px 16px",
          marginBottom: "16px",
        }}
      >
        <div style={{ fontSize: "14px", color: "#b91c1c", fontWeight: "500" }}>
          ⚠️ Unable to load payout schedule
        </div>
        <div style={{ fontSize: "12px", color: "#991b1b", marginTop: "4px" }}>
          Please refresh the page or contact support if the issue persists.
        </div>
      </div>
    );
  }

  const renderChangeModal = () => (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
      onClick={() => setShowChangeModal(false)}
    >
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          padding: "24px",
          maxWidth: "400px",
          width: "90%",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: "0 0 8px 0", fontSize: "18px" }}>
          Change Payout Day
        </h3>
        <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#6b7280" }}>
          Select a new weekday for your automatic payouts. Changes have a{" "}
          {weekdayInfo.cooldownDays}-day cooldown.
        </p>

        {error && (
          <div
            style={{
              marginBottom: "16px",
              padding: "12px",
              background: "#fee2e2",
              border: "1px solid #ef4444",
              borderRadius: "6px",
              fontSize: "14px",
              color: "#b91c1c",
            }}
          >
            {error}
          </div>
        )}

        <div style={{ marginBottom: "20px" }}>
          <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
            Select Weekday
          </label>
          <select
            value={selectedWeekday}
            onChange={(e) => setSelectedWeekday(e.target.value)}
            disabled={changing}
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
          >
            {weekdayInfo.enabledWeekdays.map((day) => (
              <option key={day} value={day}>
                {capitalizeWeekday(day)}
                {day === weekdayInfo.currentWeekday && " (current)"}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", gap: "12px" }}>
          <button
            onClick={() => setShowChangeModal(false)}
            disabled={changing}
            style={{
              flex: 1,
              padding: "10px",
              background: "white",
              color: "#6b7280",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleChangeWeekday}
            disabled={changing || selectedWeekday === weekdayInfo.currentWeekday}
            style={{
              flex: 1,
              padding: "10px",
              background: changing ? "#9ca3af" : "#2563eb",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: changing ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            {changing ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );

  if (compact) {
    return (
      <div
        style={{
          background: weekdayInfo.isPayoutDayToday ? "#ecfdf5" : "white",
          border: `1px solid ${weekdayInfo.isPayoutDayToday ? "#10b981" : "#e5e7eb"}`,
          borderRadius: "8px",
          padding: "12px 16px",
          marginBottom: "16px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "4px" }}>
              📅 Your Payout Day
            </div>
            <div style={{ fontSize: "16px", fontWeight: "600" }}>
              {capitalizeWeekday(weekdayInfo.currentWeekday)}s
            </div>
            <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
              Next: {formatDate(weekdayInfo.nextPayoutDate)}
            </div>
          </div>
          {weekdayInfo.canChangeWeekday && (
            <button
              onClick={() => setShowChangeModal(true)}
              style={{
                padding: "6px 12px",
                fontSize: "12px",
                background: "white",
                color: "#2563eb",
                border: "1px solid #2563eb",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              Change
            </button>
          )}
        </div>

        {success && (
          <div
            style={{
              marginTop: "12px",
              padding: "8px 12px",
              background: "#d1fae5",
              border: "1px solid #10b981",
              borderRadius: "6px",
              fontSize: "13px",
              color: "#047857",
            }}
          >
            ✓ {success}
          </div>
        )}

        {showChangeModal && renderChangeModal()}
      </div>
    );
  }

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        padding: "24px",
        marginBottom: "24px",
      }}
    >
      <h2 style={{ margin: "0 0 20px 0", fontSize: "20px", fontWeight: "600" }}>
        📅 Your Payout Schedule
       </h2>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "20px" }}>
        <div>
          <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "4px" }}>
            Assigned Weekday
          </div>
          <div style={{ fontSize: "24px", fontWeight: "600", color: "#1f2937" }}>
            {capitalizeWeekday(weekdayInfo.currentWeekday)}
          </div>
        </div>

        <div>
          <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "4px" }}>
            Next Payout Date
          </div>
          <div style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937" }}>
            {formatDate(weekdayInfo.nextPayoutDate)}
          </div>
        </div>
      </div>

      {success && (
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            background: "#d1fae5",
            border: "1px solid #10b981",
            borderRadius: "8px",
            fontSize: "14px",
            color: "#047857",
          }}
        >
          ✓ {success}
        </div>
      )}

      {weekdayInfo.canChangeWeekday ? (
        <button
          onClick={() => setShowChangeModal(true)}
          style={{
            padding: "10px 20px",
            background: "#2563eb",
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          Change Payout Weekday
        </button>
      ) : (
        <div
          style={{
            padding: "12px",
            background: "#fef3c7",
            border: "1px solid #f59e0b",
            borderRadius: "8px",
            fontSize: "14px",
            color: "#92400e",
          }}
        >
          ⏳ You can change your payout weekday again in {weekdayInfo.daysUntilCanChange} days
        </div>
      )}

      {showChangeModal && renderChangeModal()}
    </div>
  );
};

export default PayoutWeekdayCard;
